import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useEffect, useState } from 'react';

import { contractFilterPivotMap } from '../../constants/contracts';
import { useConfig } from '../../hooks/useConfig';
import useDebounce from '../../hooks/useDebounce';
import { getRequestJSON } from '../../util/services';

interface ContractFilterInputProps {
  pivotType: string;
  placeholder: string;
  modifyFilters: Function;
  closeFilterMenu: Function;
}

// necessary to prevent keydown selecting menu item by letter
const onKeyDown = (e) => {
  e.stopPropagation();
};

const renderOptions = (props, option) => (
  // eslint-disable-next-line react/jsx-props-no-spreading
  <li {...props} key={option.id}>
    {option.label || option.name}
  </li>
)

// used for finding filter options by Account Manager, Advertiser Name, Client Name, and Trafficker
function ContractFilterInput({
  pivotType,
  placeholder,
  modifyFilters,
  closeFilterMenu,
}: ContractFilterInputProps) {
  const { apiHost } = useConfig();

  const [isSearching, setIsSearching] = useState(false);
  const [value, setValue] = useState('');
  const [results, setResults] = useState([]);
  const debouncedSearchTerm = useDebounce(value, 500);
  // Effect for API call
  useEffect(() => {
    if (debouncedSearchTerm) {
      setIsSearching(true);
      const pathWithQuery = `${contractFilterPivotMap[pivotType].endpoint}${debouncedSearchTerm}`;
      // TODO: convert to async/await
      getRequestJSON(apiHost, pathWithQuery)
        .then((response) => {
          setIsSearching(false);
          setResults(response);
        })
        .catch(() => {
          setIsSearching(false);
        });
    } else {
      setResults([]);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, apiHost, pivotType]);

  return (
    <Autocomplete
      freeSolo
      onKeyDown={onKeyDown}
      value={value}
      options={results}
      renderOption={renderOptions}
      style={{ width: 300 }}
      onInputChange={(event, value1) => {
        setValue(value1);
      }}
      onChange={(e, value1) => {
        if (value1) {
          modifyFilters(contractFilterPivotMap[pivotType].filterName, {
            id: value1.id,
            name: value1.name,
          });
          closeFilterMenu();
        }
      }}
      renderInput={(params) => (
        <TextField
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...params}
          variant="outlined"
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end">
                {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}

export default ContractFilterInput;
